/**
 * UpgradePromptProvider.tsx
 *
 * Single owner of the C3 upgrade prompt modal.
 * Exposes triggerUpgradePrompt(reason) for any screen to call.
 *
 * Rules (all enforced here, nowhere else):
 *   - One-time ever — gated by coretrak.upgradePrompt.shownAt in localStorage
 *   - 15-minute delay after trigger
 *   - Copy selected by reason
 *   - Modal dismissed on backdrop click, "Not now", or "Unlock with Trak"
 *
 * Target: src/components/ui/UpgradePromptProvider.tsx
 */

import { createContext, useContext, useRef, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// ── Types ─────────────────────────────────────────────────────────────────────

export type UpgradePromptReason =
  | 'logging_days'
  | 'weight_loss'
  | 'gold_milestone'
  | 'weekly_report';

interface UpgradePromptCopy {
  title: string;
  body: string;
}

// ── Copy by reason ─────────────────────────────────────────────────────────────

const COPY: Record<UpgradePromptReason, UpgradePromptCopy> = {
  logging_days: {
    title: 'Good job on the logging',
    body: "You're building consistency. Unlock deeper insights into your progress and habits.",
  },
  weight_loss: {
    title: 'Fantastic job on the weight loss',
    body: "You're making real progress. Unlock deeper insight into your long-term trends.",
  },
  gold_milestone: {
    title: 'Great job on the gold milestone',
    body: "You've built real momentum. Unlock deeper insight into what's driving it.",
  },
  weekly_report: {
    title: 'Your week is ready to review',
    body: 'Unlock Trak to get weekly coaching, deeper nutrition trends, and full progress history.',
  },
};

const FLAG_KEY = 'coretrak.upgradePrompt.shownAt';
const DELAY_MS = 15 * 60 * 1000;

// ── Context ───────────────────────────────────────────────────────────────────

interface UpgradePromptContextValue {
  triggerUpgradePrompt: (reason: UpgradePromptReason, options?: { delayMs?: number }) => void;
}

const UpgradePromptContext = createContext<UpgradePromptContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function UpgradePromptProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [copy, setCopy] = useState<UpgradePromptCopy>(COPY.logging_days);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    setOpen(false);
    try { localStorage.setItem(FLAG_KEY, String(Date.now())); } catch { /* best effort */ }
  }, []);

  const triggerUpgradePrompt = useCallback((reason: UpgradePromptReason, options?: { delayMs?: number }) => {
    // One-time gate
    try { if (localStorage.getItem(FLAG_KEY)) return; } catch { return; }

    const delayMs = options?.delayMs ?? DELAY_MS;

    if (delayMs === 0) {
      // Immediate — write flag only on open, not on schedule
      try { if (localStorage.getItem(FLAG_KEY)) return; } catch { return; }
      try { localStorage.setItem(FLAG_KEY, String(Date.now())); } catch { /* best effort */ }
      setCopy(COPY[reason]);
      setOpen(true);
      return;
    }

    // Prevent duplicate timers
    if (timerRef.current) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      // Re-check flag — may have been set by another trigger during delay
      try { if (localStorage.getItem(FLAG_KEY)) return; } catch { return; }
      try { localStorage.setItem(FLAG_KEY, String(Date.now())); } catch { /* best effort */ }
      setCopy(COPY[reason]);
      setOpen(true);
    }, delayMs);
  }, []);

  return (
    <UpgradePromptContext.Provider value={{ triggerUpgradePrompt }}>
      {children}

      {open && (
        <>
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 24px',
            }}
            onClick={dismiss}
          >
            <div
              style={{
                background: '#fff', borderRadius: 20, padding: '24px 20px',
                maxWidth: 360, width: '100%',
                boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <p style={{ fontSize: 18, fontWeight: 600, color: '#1a2a2a', letterSpacing: -0.4, margin: '0 0 8px', lineHeight: 1.3 }}>
                {copy.title}
              </p>
              <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, margin: '0 0 20px' }}>
                {copy.body}
              </p>
              <button
                type="button"
                onClick={() => { dismiss(); navigate('/paywall'); }}
                style={{
                  width: '100%', background: '#007c86', color: '#fff',
                  borderRadius: 20, padding: '12px 0', fontSize: 13, fontWeight: 600,
                  border: 'none', cursor: 'pointer', marginBottom: 10,
                }}
              >
                Unlock with Trak
              </button>
              <button
                type="button"
                onClick={dismiss}
                style={{
                  width: '100%', background: 'none', color: '#9CA3AF',
                  border: 'none', cursor: 'pointer', fontSize: 13, padding: '4px 0',
                }}
              >
                Not now
              </button>
            </div>
          </div>
        </>
      )}
    </UpgradePromptContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useUpgradePrompt(): UpgradePromptContextValue {
  const ctx = useContext(UpgradePromptContext);
  if (!ctx) throw new Error('useUpgradePrompt must be used inside UpgradePromptProvider');
  return ctx;
}
